import { SearchOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { sortBy } from 'lodash';
import { type BaseSelectRef } from 'rc-select';
import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { filterOption } from 'components/choosers/helpers';
import { CustomFilterContainer, CustomFilterPopover } from 'components/data/helpers/filters.style';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlFilterBy,
  AtlasGqlFilterOperator,
  useGetWorkCampaignsChooserLazyQuery,
} from 'types/atlas-graphql';
import { isTableFilterOperator } from 'utils/constants';

import {
  FilterMultiSelectContainer,
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from './TableFilterMultiSelect';

export const CampaignFilter = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
}: CustomFilterDropdownProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<BaseSelectRef>(null);
  const [searchValue, setSearchValue] = useState('');

  const [getWorkCampaigns, { data, loading }] = useGetWorkCampaignsChooserLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const filters: AtlasGqlFilterBy[] = [
      {
        key: 'archived',
        values: [],
        operator: AtlasGqlFilterOperator.IsNull,
      },
    ];

    getWorkCampaigns({ variables: { filterBy: filters } });
  }, [getWorkCampaigns]);

  // `name` is nullable on WorkCampaign; skip those options so search (filterOption →
  // label.toString()) cannot throw, and so we never emit a null filter value.
  const campaigns = sortBy(
    data?.paginatedWorkCampaigns?.items?.filter(campaign => campaign.name) ?? [],
    ['name']
  );

  /**
   * Preset tabs seed a column's filter value with an operator sentinel instead of a value —
   * Work Orders > Unplanned seeds `parentName: ['IS_NULL']`. Antd passes that straight through
   * as a selected key, so it has to be kept out of the multiselect and out of what we emit:
   * otherwise the sentinel renders as a tag, and because paging.js reads the operator from the
   * first filter value, a selected Campaign would be sent under `IS_NULL` and silently ignored.
   */
  const operatorKeys = (selectedKeys ?? []).filter(isTableFilterOperator);
  const campaignKeys = (selectedKeys ?? []).filter(key => !isTableFilterOperator(key)) as string[];

  const handleChange = (values: string[]) => {
    // Clearing the selection leaves the preset tab's filter as we found it.
    setSelectedKeys(values.length ? values : operatorKeys);
  };

  const handleConfirm = () => {
    setSearchValue('');
    confirm({ closeDropdown: true });
  };

  // Like clearing the selection, Reset keeps the preset tab's operator, which clearFilters would drop.
  const handleReset = () => {
    setSearchValue('');
    setSelectedKeys(operatorKeys);
    confirm({ closeDropdown: true });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
  };

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover className="ant-table-filter-dropdown">
        <CustomFilterContainer>
          <FilterMultiSelectContainer>
            <TableFilterMultiSelect
              id="campaign-filter-dropdown"
              ref={inputRef}
              placeholder={t('workOrder.selectCampaign', 'Select Campaign')}
              loading={loading}
              allowClear
              showSearch
              filterOption={filterOption}
              value={loading ? undefined : campaignKeys}
              onChange={handleChange}
              searchValue={searchValue}
              onSearch={setSearchValue}
              onOpenChange={handleOpenChange}
              suffixIcon={<SearchOutlined aria-hidden />}
              options={campaigns.map(campaign => ({
                value: campaign.name,
                label: campaign.name,
                className: 'campaign-filter-option',
              }))}
            />
          </FilterMultiSelectContainer>
        </CustomFilterContainer>
        <div className="ant-table-filter-dropdown-btns">
          <Button
            type="link"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleReset)
            }
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleConfirm)
            }
          >
            {t('table.ok', 'OK')}
          </Button>
        </div>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
